import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import * as styles from './ingredient-swap-modal.module.scss';
import { config } from '../../../config';
import { removeModalAction } from '../../../redux/actions/actions-modals';
import { clearIngredientSwap } from '../../../redux/meal-planer/meal-planer.actions';
import { selectIngredientSwap } from '../../../redux/meal-planer/meal-planer.selectors';

import CustomModal from '../../../templates/custom-modal';
import Loader from '../../atoms/loader';
import Button from '../../atoms/button';

interface IIngredientSwapSource {
    positionId: number;
    recipeId: number | null;
    ingredientId: number;
}

interface IIngredientSwapProposal {
    ingredientId: number;
    name: string;
    reason: string;
    macroNote: string;
    unitId: number;
    unitValue: string;
}

interface IIngredientSwapState {
    ingredient: { id: number; name: string } | null;
    proposals: IIngredientSwapProposal[];
    report: { proposed: number; verified: number; rejected: string[] } | null;
    status: string;
}

export interface IIngredientSwapModalProps {
    modalId: number;
    source: IIngredientSwapSource;
    onConfirm(proposal: IIngredientSwapProposal): void;
}

const { apiStatusMap } = config;

const IngredientSwapModal: React.FC<IIngredientSwapModalProps> = ({ modalId, onConfirm }) => {
    const dispatch = useDispatch();
    const { ingredient, proposals, status }: IIngredientSwapState = useSelector(
        selectIngredientSwap
    );
    const [selectedId, setSelectedId] = useState<number | null>(null);

    const handleConfirm = () => {
        const selected = proposals.find((proposal) => proposal.ingredientId === selectedId);
        if (onConfirm && selected) {
            onConfirm(selected);
        }
    };
    const handleCancel = () => {
        dispatch(removeModalAction(modalId));
    };

    const handleSelect = (ingredientId: number) => {
        return () => {
            setSelectedId(ingredientId);
        };
    };

    const title = ingredient ? `Zamiennik dla: ${ingredient.name}` : 'Zamiana składnika';

    useEffect(() => {
        return () => {
            dispatch(clearIngredientSwap());
        };
    }, [dispatch]);

    return (
        <CustomModal modalId={modalId} className={styles.modal} title={title}>
            {status === apiStatusMap.loading && (
                <Loader className={styles.loaderBox} text="Szukam zamienników..." />
            )}
            {status === apiStatusMap.fail && (
                <p className={styles.error}>Nie udało się pobrać zamienników. Spróbuj ponownie.</p>
            )}
            {status === apiStatusMap.success && proposals.length === 0 && (
                <p className={styles.noContent}>
                    Brak bezpiecznych zamienników dla tego składnika.
                </p>
            )}
            {proposals.length > 0 && (
                <>
                    <ul className={styles.list}>
                        {proposals.map((proposal) => {
                            const isSelected = proposal.ingredientId === selectedId;
                            return (
                                <li key={proposal.ingredientId}>
                                    <button
                                        type="button"
                                        className={`${styles.proposal} ${
                                            isSelected ? styles.selected : ''
                                        }`}
                                        onClick={handleSelect(proposal.ingredientId)}
                                    >
                                        <span className={styles.proposalName}>{proposal.name}</span>
                                        {proposal.reason && (
                                            <span className={styles.proposalReason}>
                                                {proposal.reason}
                                            </span>
                                        )}
                                        {proposal.macroNote && (
                                            <span className={styles.proposalMacro}>
                                                {proposal.macroNote}
                                            </span>
                                        )}
                                    </button>
                                </li>
                            );
                        })}
                    </ul>
                    <div className={styles.actions}>
                        <Button
                            className={styles.button}
                            color="yellow"
                            size="small"
                            disabled={!selectedId}
                            onClick={handleConfirm}
                        >
                            Zamień
                        </Button>
                        <Button
                            className={styles.button}
                            color="blank"
                            size="small"
                            onClick={handleCancel}
                        >
                            Anuluj
                        </Button>
                    </div>
                </>
            )}
        </CustomModal>
    );
};

export default IngredientSwapModal;
